import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "whatsapp-thread-card" }
const _hoisted_2 = { class: "whatsapp-thread-card__header" }
const _hoisted_3 = { class: "whatsapp-thread-card__header-content" }
const _hoisted_4 = {
  key: 0,
  class: "whatsapp-thread-card__status-wrapper"
}
const _hoisted_5 = {
  key: 0,
  class: "whatsapp-thread-card__status"
}
const _hoisted_6 = {
  key: 1,
  class: "whatsapp-thread-card__status"
}
const _hoisted_7 = {
  key: 0,
  class: "whatsapp-thread-card__loading"
}
const _hoisted_8 = { class: "whatsapp-thread-card__loading-content" }
const _hoisted_9 = {
  key: 0,
  class: "whatsapp-thread-card__empty"
}
const _hoisted_10 = {
  key: 0,
  class: "whatsapp-thread-card__loading-older"
}
const _hoisted_11 = {
  key: 1,
  class: "whatsapp-thread-card__no-more"
}
const _hoisted_12 = ["data-index"]
const _hoisted_13 = {
  key: 0,
  class: "whatsapp-thread-card__date-separator"
}

import MessageItem from "@/components/socials/MessageItem.vue";
import type { Message, ChatParticipant, Conversation } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { computed, ref, type ComputedRef } from "vue";
import { useVirtualizer } from "@tanstack/vue-virtual";
import { useMessageScroll } from "@/composables/messages/useMessageScroll";

interface LegacyMessage {
  type: "outgoing" | "incoming";
  author: string;
  timestamp: string | Date;
  content: string;
}

interface Props {
  contactName?: string;
  contactId?: string;
  lastSeen?: string;
  messages?: LegacyMessage[];
  unipileMessages?: Message[];
  isLoading?: boolean;
  isLoadingOlderMessages?: boolean;
  hasMoreMessages?: boolean;
  participants?: ChatParticipant[];
  conversation?: Conversation | null;
  showBackButton?: boolean;
  highlightMessageId?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsAppThreadCard',
  props: {
    contactName: { default: "" },
    contactId: { default: "" },
    lastSeen: { default: "" },
    messages: { default: () => [] },
    unipileMessages: { default: () => [] },
    isLoading: { type: Boolean, default: false },
    isLoadingOlderMessages: { type: Boolean, default: false },
    hasMoreMessages: { type: Boolean, default: true },
    participants: {},
    conversation: { default: null },
    showBackButton: { type: Boolean, default: false },
    highlightMessageId: { default: null }
  },
  emits: ["reply", "ai-reply", "add-followup", "forward", "delete", "react", "remove-reaction", "view-contact-profile", "load-older-messages", "back"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const handleContactNameClick = () => {
  if (props.contactId) {
    emit("view-contact-profile", props.contactId);
  }
};

const parentRef = ref<HTMLElement | null>(null);

const unipileMessages = computed(() => {
  let messages: Message[];
  if (props.unipileMessages && props.unipileMessages.length > 0) {
    messages = props.unipileMessages;
  } else {
    messages = props.messages.map((msg) => ({
      id: `legacy-${msg.timestamp}`,
      conversationId: "",
      senderId: msg.type === "outgoing" ? "me" : "contact",
      senderName: msg.author,
      content: msg.content,
      channel: "whatsapp",
      timestamp: typeof msg.timestamp === "string" ? msg.timestamp : msg.timestamp.toISOString(),
      isOwn: msg.type === "outgoing",
    })) as Message[];
  }
  return messages;
});

const shouldShowDateSeparator = (index: number): boolean => {
  if (index === 0) return true;
  const message = unipileMessages.value[index];
  const prevMessage = unipileMessages.value[index - 1];
  if (!message || !prevMessage) return false;
  const currentDate = new Date(message.timestamp).toDateString();
  const previousDate = new Date(prevMessage.timestamp).toDateString();
  return currentDate !== previousDate;
};

const formatDateSeparator = (timestamp: string): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "TODAY";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "YESTERDAY";
  }
  return date
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .toUpperCase();
};

const conversationKey = computed(() => {
  if (props.conversation?.id) {
    return props.conversation.id;
  }
  if (unipileMessages.value.length > 0 && unipileMessages.value[0].conversationId) {
    return unipileMessages.value[0].conversationId;
  }
  return "default";
});

const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: unipileMessages.value.length,
    getScrollElement: () => parentRef.value,
    estimateSize: () => 72,
    overscan: 8,
    getItemKey: (index: number) => {
      const msg = unipileMessages.value[index] as any;
      return msg?._stableKey || msg?.id || index;
    },
  }))
);

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

const measureElement = (el: HTMLElement | null, _index: number) => {
  if (el) {
    rowVirtualizer.value.measureElement(el);
  }
};

const { handleScroll, highlightedMessageId } = useMessageScroll({
  parentRef,
  rowVirtualizer: rowVirtualizer as any,
  virtualRows,
  messages: unipileMessages as ComputedRef<Array<{ id: string }>>,
  conversationKey,
  isLoading: computed(() => props.isLoading),
  isLoadingOlderMessages: computed(() => props.isLoadingOlderMessages),
  hasMoreMessages: computed(() => props.hasMoreMessages),
  onLoadOlderMessages: () => emit("load-older-messages"),
  highlightMessageId: computed(() => props.highlightMessageId),
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (__props.showBackButton)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "whatsapp-thread-card__back-btn",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('back')))
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:chevron-left" })
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h5", {
          class: "whatsapp-thread-card__contact-name whatsapp-thread-card__contact-name--clickable",
          onClick: handleContactNameClick,
          title: "View profile"
        }, _toDisplayString(__props.contactName || "Contact Name"), 1),
        (__props.lastSeen || __props.conversation?.isActive)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (__props.conversation?.isActive)
                ? (_openBlock(), _createElementBlock("span", _hoisted_5, [...(_cache[3] || (_cache[3] = [
                    _createElementVNode("span", { class: "whatsapp-thread-card__status-dot whatsapp-thread-card__status-dot--active" }, null, -1),
                    _createTextVNode(" Active now ", -1)
                  ]))]))
                : (__props.lastSeen)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                      _cache[4] || (_cache[4] = _createElementVNode("span", { class: "whatsapp-thread-card__status-dot" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(__props.lastSeen), 1)
                    ]))
                  : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (__props.isLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createVNode(LoadingBars, { size: "sm" })
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          ref_key: "parentRef",
          ref: parentRef,
          class: "whatsapp-thread-card__messages",
          onScroll: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(handleScroll) && _unref(handleScroll)(...args)))
        }, [
          (unipileMessages.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:message-outline",
                  class: "whatsapp-thread-card__empty-icon"
                }),
                _cache[5] || (_cache[5] = _createElementVNode("p", null, "No messages yet", -1))
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (__props.isLoadingOlderMessages)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:loading",
                        class: "whatsapp-thread-card__loading-older-icon"
                      }),
                      _cache[6] || (_cache[6] = _createElementVNode("span", null, "Loading...", -1))
                    ]))
                  : (!__props.hasMoreMessages && unipileMessages.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [...(_cache[7] || (_cache[7] = [
                        _createElementVNode("span", null, "No more messages", -1)
                      ]))]))
                    : _createCommentVNode("", true),
                _createElementVNode("div", {
                  style: _normalizeStyle({
            height: `${totalSize.value}px`,
            width: '100%',
            position: 'relative',
          })
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(virtualRows.value, (virtualRow) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: unipileMessages.value[virtualRow.index]?.id || virtualRow.index,
                      ref_for: true,
                      ref: (el) => measureElement(el as HTMLElement | null, virtualRow.index),
                      "data-index": virtualRow.index,
                      style: _normalizeStyle({
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            })
                    }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["whatsapp-thread-card__message-wrapper", {
                'whatsapp-thread-card__message-wrapper--highlighted':
                  _unref(highlightedMessageId) === unipileMessages.value[virtualRow.index]?.id,
              }])
                      }, [
                        (shouldShowDateSeparator(virtualRow.index))
                          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                              _createElementVNode("span", null, _toDisplayString(formatDateSeparator(unipileMessages.value[virtualRow.index].timestamp)), 1)
                            ]))
                          : _createCommentVNode("", true),
                        _createVNode(MessageItem, {
                          message: unipileMessages.value[virtualRow.index],
                          platform: "whatsapp",
                          "show-sender": true,
                          "show-read-receipts": true,
                          participants: __props.participants,
                          onReply: ($event: any) => (_ctx.$emit('reply', unipileMessages.value[virtualRow.index])),
                          onAiReply: ($event: any) => (_ctx.$emit('ai-reply', unipileMessages.value[virtualRow.index])),
                          onAiFollowup: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('add-followup'))),
                          onForward: ($event: any) => (_ctx.$emit('forward', unipileMessages.value[virtualRow.index])),
                          onDelete: ($event: any) => (_ctx.$emit('delete', unipileMessages.value[virtualRow.index])),
                          onReact: ($event: any) => (_ctx.$emit('react', unipileMessages.value[virtualRow.index], $event)),
                          onRemoveReaction: ($event: any) => (_ctx.$emit('remove-reaction', unipileMessages.value[virtualRow.index]))
                        }, null, 8, ["message", "participants", "onReply", "onAiReply", "onForward", "onDelete", "onReact", "onRemoveReaction"])
                      ], 2)
                    ], 12, _hoisted_12))
                  }), 128))
                ], 4)
              ], 64))
        ], 544))
  ]))
}
}

})